import React, { useEffect, useState, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Field } from "formik";
import * as yup from "yup";
import { Form, Button, Col, Row } from "react-bootstrap";
import "./style.css";
import ErrorMessageComponent from "../../modules/common/ErrorMessageComponent";
import SubmitButton from "../../modules/common/SubmitButton";
import { useDispatch, useSelector } from "react-redux";
import { errorMessage } from "../../utlis/common";
import {
  FetchInvoiceHeaders,
  addInvoiceHeaders,
  uploadInvoiceCsv,
  logFailedInvoiceUpload,
} from "../../redux/features/invoice/invoiceAction";
import moment from "moment";
import {
  dateFormats,
  invoiceDbMappingFields,
} from "../../utlis/invoiceDbMappingFields";
import {
  setCloseInvoiceModal,
  setOpenDocumentUploadModal,
  setOpenInvalidRowsModal,
  setOpenInvoiceUploadModal,
} from "../../redux/features/invoice/invoiceSlice";

interface InvoiceModalProps {
  show: boolean;
  handleClose: () => void;
  data: any[];
  headers: string[];
  metadata?: any;
  isEditMode?: boolean;
  rawFile?: File;
}

function excelSerialToDate(serial: number): string {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30));
  const date = new Date(excelEpoch.getTime() + serial * 86400 * 1000);
  return moment(date).format("DD/MM/YYYY");
}

interface FieldToCheck {
  key: string;
  metaKey: string;
  name: string;
  isSum: boolean;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ show, handleClose, data, headers, rawFile }) => {
  const dispatch: any = useDispatch();
  // fire-and-forget: log a rejected upload attempt (with the original file) for later debugging
  const logRejectedUpload = useCallback((reason: string, message: string, invoiceNumber?: string) => {
    dispatch(logFailedInvoiceUpload({ file: rawFile, reason, message, invoice_number: invoiceNumber }));
  }, [dispatch, rawFile]);

  const { shippingData } = useSelector((state: any) => state.invoiceSlice);
  const metadata = shippingData;
  const [loading, setLoading] = useState(false);
  const [existingMapping, setExistingMapping] = useState<{ [key: string]: string }>({});
  const [savedHeaders, setSavedHeaders] = useState<string[]>([]);
  const [supplierType, setSupplierType] = useState<string>("");
  const isBoat = metadata?.transport_type === "boat";

  useEffect(() => {
    if (show) {
      (async () => {
        try {
          const result: any = await dispatch(FetchInvoiceHeaders({}));
          if (result?.payload) {
            const mapping = result.payload;
            setExistingMapping(mapping);

            const headersFromMapping = Object.values(mapping)
              .filter((val): val is string => typeof val === "string" && val !== "")
              .filter(Boolean);

            setSavedHeaders(headersFromMapping); const tipo = result?.payload?.supplier?.country_of_manufacturer?.toLowerCase() || "";
            setSupplierType(tipo);
          }
        } catch (err) {
          setExistingMapping({});
          setSavedHeaders([]);
        }
      })();
    }
  }, [show, dispatch]);

  const initialValues = {
    mappedFields: Object.values(invoiceDbMappingFields).reduce((acc, meta) => {
      acc[meta.key] = existingMapping?.[meta.key] || "";
      return acc;
    }, {} as { [key: string]: string }),
  };

  const tipoValidationConfig: Record<
    string,
    { required: string[]; optional: string[] }
  > = {
    "tipo-1": {
      required: ["number_pallets"],
      optional: ["number_of_box", "gross_weight", "net_weight", "bill_lading_number", "custom_code"],
    },
    "tipo-2": {
      required: ["number_of_box", "gross_weight", "net_weight", "number_pallets", "custom_code"],
      optional: ["bill_lading_number"],
    },
    "tipo-3": {
      required: ["number_of_box", "gross_weight", "net_weight", "number_pallets", "custom_code", "bill_lading_number", "container_id"],
      optional: [],
    },
  };

  const getTipoValidationSchema = useCallback(() => {
    const tipo = supplierType.toLowerCase();
    const tipoConfig = tipoValidationConfig[tipo] || { required: [], optional: [] };

    return yup.object().shape({
      mappedFields: yup.object().shape(
        Object.values(invoiceDbMappingFields).reduce((acc, meta) => {
          const key = meta.key;
          if (tipoConfig.required.includes(key)) {
            acc[key] = yup
              .string()
              .required(`${meta.name} mapping is mandatory for this shipment`);
          } else if (tipoConfig.optional.includes(key)) {
            acc[key] = yup.string();
          } else if (meta.key === "bill_lading_number") {
            acc[key] = isBoat
              ? yup.string().required(`${meta.name} mapping is mandatory for boat shipment`)
              : yup.string();
          } else if (meta.key === "container_id") {
            acc[key] = isBoat
              ? yup.string().required(`${meta.name} mapping is mandatory for boat shipment`)
              : yup.string();
          }
          else {
            acc[key] = meta.required
              ? yup.string().required(`${meta.name} mapping is mandatory`)
              : yup.string();
          }
          return acc;
        }, {} as { [key: string]: any })
      ),
    });
  }, [supplierType, isBoat]);

  const validationSchema = getTipoValidationSchema();

  const validateDateField = useCallback((formKey: string, headerKey: string, label: string, errors: any) => {
    for (let i = 0; i < data.length; i++) {
      const raw = data[i][headerKey];
      let trimmed: string | undefined;
      if (raw === undefined || raw === null || raw === "") continue;

      if (typeof raw === "number") {
        trimmed = excelSerialToDate(raw);
      } else if (typeof raw === "string") {
        trimmed = raw.trim();
      } else {
        continue;
      }
      if (!trimmed) continue;
      const isValid = moment(trimmed, dateFormats, true).isValid();

      if (!isValid) {
        errors.mappedFields = errors.mappedFields || {};
        errors.mappedFields[formKey] = `${label} not valid`;
        break;
      }
    }
  }, [data]);

  const handleBack = () => {
    dispatch(setCloseInvoiceModal());
    dispatch(setOpenInvoiceUploadModal({}));
  };

  const handleSubmit = async (values: typeof initialValues, { resetForm, setErrors }: { resetForm: () => void; setErrors: any }) => {
    try {
      setLoading(true);
      const errors: any = {};
      if (!metadata) {
        errorMessage("Metadata not found from previous step. Please upload the file again.");
        dispatch(setCloseInvoiceModal());
        dispatch(setOpenInvoiceUploadModal({}));
        setLoading(false);
        return;
      }

      const {
        insertion_date,
        bill_lading_number: billLadingField,
        number_of_box: boxesField,
        gross_weight: grossField,
        net_weight: netField,
        number_pallets: palletsField,
      } = values.mappedFields;

      // Validate insertion date if mapped
      if (insertion_date) {
        validateDateField("insertion_date", insertion_date, "Insertion Date", errors);
      }

      // Validate number_pallets is mapped if required
      if (!palletsField && tipoValidationConfig[supplierType.toLowerCase()]?.required.includes("number_pallets")) {
        errors.mappedFields = errors.mappedFields || {};
        errors.mappedFields.number_pallets = "Number of pallets mapping is mandatory";
      }

      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        errorMessage("Please correct the mapping errors and try again.");
        dispatch(setCloseInvoiceModal());
        dispatch(setOpenInvoiceUploadModal({}));
        setLoading(false);
        return;
      }

      // Bill of lading validation if boat and mapped
      if (isBoat && billLadingField) {
        const bills = data.map((row) => (row[billLadingField]?.toString().trim() || "").toUpperCase());
        const nonEmptyBills = bills.filter((b) => b);
        if (bills.length !== nonEmptyBills.length) {
          errors.mappedFields = errors.mappedFields || {};
          errors.mappedFields.bill_lading_number = "All rows must have bill of lading";
          setErrors(errors);
          errorMessage("All rows must have a bill of lading. Please upload a corrected file.");
          dispatch(setCloseInvoiceModal());
          dispatch(setOpenInvoiceUploadModal({}));
          setLoading(false);
          return;
        }
        if (nonEmptyBills.length > 0) {
          const uniqueBills = Array.from(new Set(nonEmptyBills));
          if (uniqueBills.length > 1) {
            errors.mappedFields = errors.mappedFields || {};
            errors.mappedFields.bill_lading_number = "All bill of lading must be the same";
            setErrors(errors);
            errorMessage("All bill of lading values must be the same. Please upload a corrected file.");
            dispatch(setCloseInvoiceModal());
            dispatch(setOpenInvoiceUploadModal({}));
            setLoading(false);
            return;
          } else if (uniqueBills[0] !== (metadata.bill_of_lading || "").toUpperCase()) {
            errors.mappedFields = errors.mappedFields || {};
            errors.mappedFields.bill_lading_number = "Bill of lading does not match provided value";
            setErrors(errors);
            errorMessage("Bill of lading does not match provided value. Please upload a corrected file.");
            dispatch(setCloseInvoiceModal());
            dispatch(setOpenInvoiceUploadModal({}));
            setLoading(false);
            return;
          }
        }
      }

      const mappedData: any[] = data
        .map((item) => {
          const mappedItem: { [key: string]: any } = {};
          Object.keys(values.mappedFields).forEach((targetField) => {
            const sourceField = values.mappedFields[targetField];
            let rawValue = item[sourceField];

            if (["insertion_date"].includes(targetField)) {
              if (typeof rawValue === "number") {
                rawValue = excelSerialToDate(rawValue);
              } else if (typeof rawValue === "string") {
                rawValue = rawValue.trim();
              }
            }

            if (sourceField && rawValue !== undefined) {
              mappedItem[targetField] = rawValue;
            }
          });

          // Add fixed fields from metadata
          mappedItem.invoice_number = metadata.invoice_number;
          mappedItem.invoice_date = metadata.invoice_date;
          mappedItem.expected_delivery_date = metadata.expected_delivery_date;
          mappedItem.currency = metadata.currency;
          mappedItem.shipping_mode = metadata.transport_type;

          // Set bill of lading if boat and not mapped (fallback)
          if (isBoat && metadata.bill_of_lading && !mappedItem.bill_lading_number) {
            mappedItem.bill_lading_number = metadata.bill_of_lading;
          }

          return mappedItem;
        })
        .filter((row) => row.article_code && row.order_number); // Skip if any are missing

      if (mappedData.length === 0) {
        errorMessage("No valid rows found after mapping. Please upload a corrected file.");
        dispatch(setCloseInvoiceModal());
        dispatch(setOpenInvoiceUploadModal({}));
        setLoading(false);
        return;
      }

      // Validate fields against metadata
      const fieldsToCheck: FieldToCheck[] = [
        { key: "number_of_box", metaKey: "number_boxes", name: "Number of boxes", isSum: true },
        { key: "gross_weight", metaKey: "gross_weight", name: "Gross weight", isSum: true },
        { key: "net_weight", metaKey: "net_weight", name: "Net weight", isSum: true },
        { key: "number_pallets", metaKey: "number_pallets", name: "Number of pallets", isSum: false },
        { key: "container_id", metaKey: "container_id", name: "Container id", isSum: false },
      ];

      const tolerance = 0.01;

      for (const { key, metaKey, name, isSum } of fieldsToCheck) {
        const metaValue = metadata[metaKey];
        if (metaValue !== undefined && metaValue !== "" && values.mappedFields[key]) {
          if (isSum) {
            // Sum validation for number_of_box, gross_weight, net_weight
            const sum = mappedData.reduce((s, row) => s + (Number(row[key]) || 0), 0);
            if (Math.abs(sum - Number(metaValue)) > tolerance) {
              const msg = `${name} mismatch: calculated sum from file ${sum.toFixed(2)}, provided in metadata ${metaValue}. Please upload a corrected file.`;
              logRejectedUpload(`${key}_mismatch`, msg, metadata?.invoice_number);
              errorMessage(msg);
              dispatch(setCloseInvoiceModal());
              dispatch(setOpenInvoiceUploadModal({}));
              setLoading(false);
              return;
            }
          } else {
            let valuesList = mappedData
              .map((row) => row[key])
              .filter((val) => val !== undefined && val !== "");

            // Special case: Container (may contain comma separated multiple per row)
            if (name.toLowerCase().includes("container")) {
              valuesList = valuesList.flatMap((v) =>
                String(v).split(",").map((c) => c.trim())
              );
            }

            const uniqueValues = Array.from(new Set(valuesList));
            const uniqueCount = uniqueValues.length;
            if (uniqueCount !== Number(metaValue)) {
              const msg = `${name} mismatch: unique count from file ${uniqueCount}, provided in metadata ${metaValue}. Please upload a corrected file.`;
              logRejectedUpload(`${key}_mismatch`, msg, metadata?.invoice_number);
              errorMessage(msg);
              dispatch(setCloseInvoiceModal());
              dispatch(setOpenInvoiceUploadModal({}));
              setLoading(false);
              return;
            }
          }
        }
      }

      const headersPayload = values.mappedFields;

      try {
        const uploadResult: any = await dispatch(uploadInvoiceCsv(mappedData)).unwrap();
        const inserted = uploadResult?.data?.inserted || 0;
        const invalidRows = uploadResult?.data?.invalidRows || [];
        const quantityErrors: any[] = uploadResult?.data?.quantityErrors || [];

        if (inserted > 0) {
          await dispatch(addInvoiceHeaders(headersPayload));
        }

        if (quantityErrors.length > 0) {
          dispatch(setCloseInvoiceModal());
          dispatch(
            setOpenInvalidRowsModal({
              data: quantityErrors.map((e: any, i: number) => ({
                row: i + 1,
                errors: [{
                  reason: e.reason,
                  key: "quantity",
                  value: e.uploading,
                  order_number: e.order_number,
                  article_code: e.article_code,
                }],
              })),
              headers: [],
            })
          );
          setLoading(false);
          return;
        }

        if (invalidRows.length > 0) {
          dispatch(setCloseInvoiceModal());
          dispatch(
            setOpenInvalidRowsModal({
              data: invalidRows,
              headers: Object.keys(invalidRows[0]?.row || {}),
            })
          );
          setLoading(false);
          return;
        }

        if (inserted > 0 && invalidRows.length === 0) {
          dispatch(setCloseInvoiceModal());
          const currentInvoiceNumber = metadata.invoice_number || "";
          // Optionally proceed to document upload if needed
          // dispatch(setOpenDocumentUploadModal({
          //   data: mappedData,
          //   headers: Object.keys(mappedData[0] || {}),
          //   invoiceNumber: currentInvoiceNumber,
          // }));
          setLoading(false);
          resetForm();
          return;
        }

        // No valid rows
        if (inserted === 0) {
          errorMessage("No valid rows found in the file. Please check the formatting and content of the columns and upload again.");
          dispatch(setCloseInvoiceModal());
          dispatch(setOpenInvoiceUploadModal({}));
          setLoading(false);
          return;
        }
      } catch (error: any) {
        console.error("Upload error:", error);
        errorMessage("Failed to upload invoice data. Please upload the file again.");
        dispatch(setCloseInvoiceModal());
        dispatch(setOpenInvoiceUploadModal({}));
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("Failed to upload inventory:", error);
      errorMessage("An unexpected error occurred. Please upload the file again.");
      dispatch(setCloseInvoiceModal());
      dispatch(setOpenInvoiceUploadModal({}));
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="custom-modal-width">
      <Modal.Header closeButton className="modalHeader text-white">
        <Button onClick={handleBack} className="me-2">
          ← Back
        </Button>
        <Modal.Title>Map Invoice Data</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <Formik
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          initialValues={{
            mappedFields: Object.values(invoiceDbMappingFields).reduce((acc, meta) => {
              acc[meta.key] = existingMapping?.[meta.key] || "";
              return acc;
            }, {} as { [key: string]: string }),
          }}
        >
          {({ handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <div className="scroller">
                <Row className="mb-3">
                  <Col xs={5}>
                    <strong>Field</strong>
                  </Col>
                  <Col xs={2}></Col>
                  <Col xs={5}>
                    <strong>Map to</strong>
                  </Col>
                </Row>

                {Object.entries(invoiceDbMappingFields).map(([label, meta], index) => (
                  <Row key={index} className="mb-4 align-items-start">
                    <Col xs={5}>
                      <div className="static-field-info">
                        <Form.Label className="form-control static-field">{label}</Form.Label>
                        <small className="text-muted d-block">{meta?.description}</small>
                        <small className="text-muted d-block">{meta?.controls}</small>
                      </div>
                    </Col>
                    <Col xs={2} className="text-center">
                      <i className="bi bi-arrow-right"></i>
                    </Col>
                    <Col xs={5}>
                      <Field as="select" name={`mappedFields.${meta.key}`} className="form-select">
                        <option value="">Select a field</option>
                        {(() => {
                          const displayHeaders = headers && headers.length > 0 ? headers : savedHeaders;
                          return displayHeaders.map((header) => (
                            <option key={header} value={header}>{header}</option>
                          ));
                        })()}
                      </Field>
                      <ErrorMessageComponent name={`mappedFields.${meta.key}`} />
                    </Col>
                  </Row>
                ))}
              </div>
              <div className="d-flex justify-content-end mt-10">

                <Button variant="secondary" onClick={handleClose} className="me-2 custom-close-btn">
                  Cancel
                </Button>
                <SubmitButton title="Upload" type="submit" loading={loading} />
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default InvoiceModal;
